import React, { useState, useEffect } from "react";
import {
  Button,
  TextField,
  Modal,
  Box,
  Typography,
  Slide,
  Grid,
  Card,
  CardContent,
  FormControl,
  CircularProgress,
  InputAdornment,
  Autocomplete,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ErrorIcon from "@mui/icons-material/Error";
import SearchIcon from "@mui/icons-material/Search";
import { TransitionGroup } from "react-transition-group";
import Collapse from "@mui/material/Collapse";
import { keyframes } from "@mui/system";
import {
  leadsApis,
  LeadSourceSettingsApi,
  LeadAgentsSettingsApi,
} from "../../Data/axios";
import { categoryType } from "../../utils/customers";

// Define the scaling animation for the CheckIcon
const scaleAnimation = keyframes`
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.2);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

// Define common dropdown styles for consistency
const dropdownMenuStyles = {
  backgroundColor: "#333",
  color: "#fff",
  borderRadius: "8px",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
  maxHeight: "300px",
  "& .MuiAutocomplete-option, & .MuiMenuItem-root": {
    padding: "10px 16px",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#555",
    },
    "&[aria-selected='true'], &.Mui-selected": {
      backgroundColor: "#444",
      color: "#fff",
    },
  },
};

const dropdownInputStyles = {
  backgroundColor: "#f9f9f9",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#e0e0e0",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#bdbdbd",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#1d82f5",
  },
  fontFamily: "Ubuntu, sans-serif",
  color: "#333",
  "& .MuiInputBase-input": {
    color: "#333",
  },
  "& .MuiAutocomplete-endAdornment": {
    "& .MuiSvgIcon-root": {
      color: "#000",
    },
  },
};

const countryCodes = [
  { name: "India", code: "+91", length: 10, example: "12345 67890" },
  { name: "United States", code: "+1", length: 10, example: "123-456-7890" },
  { name: "United Kingdom", code: "+44", length: 10, example: "1234 567 890" },
  { name: "Canada", code: "+1", length: 10, example: "123-456-7890" },
  { name: "Australia", code: "+61", length: 9, example: "123 456 789" },
  { name: "Germany", code: "+49", length: 11, example: "123 4567 8901" },
  { name: "France", code: "+33", length: 9, example: "123 456 789" },
  { name: "Japan", code: "+81", length: 10, example: "123 456 7890" },
  { name: "China", code: "+86", length: 11, example: "123 4567 8901" },
  { name: "Brazil", code: "+55", length: 11, example: "123 456 78901" },
];

const pincodeFormats = {
  "India": { regex: /^\d{6}$/, example: "123456", message: "Please enter a valid pincode for India (6 digits)." },
  "United States": { regex: /^\d{5}$/, example: "12345", message: "Please enter a valid pincode for the United States (5 digits)." },
  "United Kingdom": { regex: /^[A-Z]{1,2}\d{1,2} \d[A-Z]{2}$/, example: "SW1A 1AA", message: "Please enter a valid pincode for the United Kingdom (e.g., SW1A 1AA)." },
  "Canada": { regex: /^[A-Z]\d[A-Z] \d[A-Z]\d$/, example: "K1A 0B1", message: "Please enter a valid pincode for Canada (e.g., K1A 0B1)." },
  "Australia": { regex: /^\d{4}$/, example: "1234", message: "Please enter a valid pincode for Australia (4 digits)." },
  "Germany": { regex: /^\d{5}$/, example: "12345", message: "Please enter a valid pincode for Germany (5 digits)." },
  "France": { regex: /^\d{5}$/, example: "12345", message: "Please enter a valid pincode for France (5 digits)." },
  "Japan": { regex: /^\d{3}-\d{4}$/, example: "123-4567", message: "Please enter a valid pincode for Japan (e.g., 123-4567)." },
  "China": { regex: /^\d{6}$/, example: "123456", message: "Please enter a valid pincode for China (6 digits)." },
  "Brazil": { regex: /^\d{5}-\d{3}$/, example: "12345-678", message: "Please enter a valid pincode for Brazil (e.g., 12345-678)." },
  default: { regex: /^\d{5,}$/, example: "12345", message: "Please enter a valid pincode (at least 5 digits)." },
};

const languages = [
  "Assamese", "Bengali", "Bodo", "Dogri", "Gujarati", "Hindi", "Kannada", "Kashmiri",
  "Konkani", "Maithili", "Malayalam", "Manipuri", "Marathi", "Nepali", "Odia",
  "Punjabi", "Sanskrit", "Santali", "Sindhi", "Tamil", "Telugu", "Urdu",
  "English", "Spanish", "French", "German", "Mandarin Chinese", "Japanese",
  "Arabic", "Russian", "Portuguese", "Italian",
].sort();

export default function AddLeads({ open, handleClose, onLeadAdded }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState(countryCodes[0]);
  const [language, setLanguage] = useState("");
  const [filteredLanguages, setFilteredLanguages] = useState(languages);
  const [languageInput, setLanguageInput] = useState("");
  const [role, setRole] = useState("");
  const [roleInput, setRoleInput] = useState("");
  const [leadAgent, setLeadAgent] = useState("");
  const [leadAgentInput, setLeadAgentInput] = useState("");
  const [leadSource, setLeadSource] = useState("");
  const [leadSourceInput, setLeadSourceInput] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [stateInput, setStateInput] = useState("");
  const [country, setCountry] = useState("");
  const [countryInput, setCountryInput] = useState("");
  const [filteredCountries, setFilteredCountries] = useState([]);
  const [countryCodeISO, setCountryCodeISO] = useState("");
  const [pincode, setPincode] = useState("");
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [filteredStates, setFilteredStates] = useState([]);
  const [hasStates, setHasStates] = useState(true);
  const [stateLoading, setStateLoading] = useState(false);
  const [leadAgents, setLeadAgents] = useState([]);
  const [filteredLeadAgents, setFilteredLeadAgents] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [filteredLeadSources, setFilteredLeadSources] = useState([]);
  const [pageValue] = useState(1);
  const [rowsPerPage] = useState(10);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const leadSourceOptions =
    leadSources?.map((source) => ({
      id: source.id,
      name: source.source,
    })) || [];

  const leadAgentOptions =
    leadAgents?.map((agent) => ({
      id: agent.id,
      name: agent.agents,
    })) || [];

  const categoryOptions = Object.keys(categoryType).map((key) => ({
    id: key,
    name: categoryType[key],
  }));

  useEffect(() => {
    fetchCountries();
    fetchLeadAgents();
    fetchLeadSources();
  }, []);

  useEffect(() => {
    if (countryCodeISO) {
      setStateLoading(true);
      fetchStates(countryCodeISO).finally(() => setStateLoading(false));
    } else {
      setStates([]);
      setFilteredStates([]);
      setState("");
      setHasStates(true);
      setStateLoading(false);
    }
  }, [countryCodeISO]);

  useEffect(() => {
    setFilteredCountries(countries);
  }, [countries]);

  useEffect(() => {
    setFilteredStates(states);
  }, [states]);

  useEffect(() => {
    setFilteredLeadAgents(leadAgentOptions);
    setFilteredLeadSources(leadSourceOptions);
  }, [leadAgents, leadSources]);

  async function fetchCountries() {
    try {
      const response = await fetch("https://cart.hostinger.com/api/v1/geolocation/countries");
      const data = await response.json();
      if (data.status === 200 && data.success) {
        const countryList = Object.entries(data.data).map(([code, name]) => ({
          code,
          name,
        }));
        setCountries(countryList);
      }
    } catch (error) {
      console.error("Error fetching countries:", error);
    }
  }

  async function fetchStates(countryCodeISO) {
    try {
      const response = await fetch(
        `https://cart.hostinger.com/api/v1/geolocation/countries/${countryCodeISO}/states`
      );
      const data = await response.json();
      if (data.status === 200 && data.success) {
        const stateList = Object.values(data.data);
        setStates(stateList);
        setFilteredStates(stateList);
        setHasStates(stateList.length > 0);
      } else {
        setStates([]);
        setFilteredStates([]);
        setHasStates(false);
      }
    } catch (error) {
      console.error("Error fetching states:", error);
      setStates([]);
      setFilteredStates([]);
      setHasStates(false);
    }
  }

  async function fetchLeadAgents() {
    try {
      const response = await LeadAgentsSettingsApi.getAll(pageValue, rowsPerPage);
      setLeadAgents(response?.data || []);
    } catch (error) {
      console.error("Error fetching lead agents:", error);
    }
  }

  async function fetchLeadSources() {
    try {
      const response = await LeadSourceSettingsApi.getAll(pageValue, rowsPerPage);
      setLeadSources(response?.data || []);
    } catch (error) {
      console.error("Error fetching lead sources:", error);
    }
  }

  const handleCountryCodeChange = (event, newValue) => {
    setCountryCode(newValue || countryCodes[0]);
    setPhone("");
    setErrors((prev) => ({ ...prev, phone: "" }));
  };

  const handleRoleChange = (event, newValue) => {
    setRole(newValue ? newValue.id : "");
    setRoleInput(newValue ? newValue.name : "");
    setErrors((prev) => ({ ...prev, role: "" }));
  };

  const handleRoleInputChange = (event, newValue) => {
    setRoleInput(newValue);
    setRole("");
    const filtered = categoryOptions.filter((option) =>
      option.name.toLowerCase().includes(newValue.toLowerCase())
    );
    if (newValue) {
      setErrors((prev) => ({ ...prev, role: "" }));
    }
  };

  const handleLeadAgentChange = (event, newValue) => {
    setLeadAgent(newValue ? newValue.id : "");
    setLeadAgentInput(newValue ? newValue.name : "");
    setErrors((prev) => ({ ...prev, leadAgent: "" }));
  };

  const handleLeadAgentInputChange = (event, newValue) => {
    setLeadAgentInput(newValue);
    setLeadAgent("");
    const filtered = leadAgentOptions.filter((agent) =>
      agent.name.toLowerCase().includes(newValue.toLowerCase())
    );
    setFilteredLeadAgents(filtered);
    if (newValue) {
      setErrors((prev) => ({ ...prev, leadAgent: "" }));
    }
  };

  const handleLeadSourceChange = (event, newValue) => {
    setLeadSource(newValue ? newValue.id : "");
    setLeadSourceInput(newValue ? newValue.name : "");
    setErrors((prev) => ({ ...prev, leadSource: "" }));
  };

  const handleLeadSourceInputChange = (event, newValue) => {
    setLeadSourceInput(newValue);
    setLeadSource("");
    const filtered = leadSourceOptions.filter((source) =>
      source.name.toLowerCase().includes(newValue.toLowerCase())
    );
    setFilteredLeadSources(filtered);
    if (newValue) {
      setErrors((prev) => ({ ...prev, leadSource: "" }));
    }
  };

  const handleCountryChange = (event, newValue) => {
    setCountry(newValue ? newValue.name : "");
    setCountryInput(newValue ? newValue.name : "");
    setState("");
    setPincode("");
    setErrors((prev) => ({ ...prev, country: "", state: "", pincode: "" }));
    const selectedCountry = countries.find((c) => c.name === newValue?.name);
    if (selectedCountry) {
      setCountryCodeISO(selectedCountry.code);
    } else {
      setCountryCodeISO("");
    }
  };

  const handleCountryInputChange = (event, newValue) => {
    setCountryInput(newValue);
    const filtered = countries.filter((c) =>
      c.name.toLowerCase().includes(newValue.toLowerCase())
    );
    setFilteredCountries(filtered);
    if (newValue) {
      setErrors((prev) => ({ ...prev, country: "" }));
    }
  };

  const handleStateChange = (event, newValue) => {
    setState(newValue || "");
    setStateInput(newValue || "");
    if (!newValue) {
      setErrors((prev) => ({ ...prev, state: "State is required." }));
    } else {
      setErrors((prev) => ({ ...prev, state: "" }));
    }
  };

  const handleStateInputChange = (event, newValue) => {
    setStateInput(newValue);
    if (hasStates) {
      const filtered = states.filter((s) =>
        s.toLowerCase().includes(newValue.toLowerCase())
      );
      setFilteredStates(filtered);
      if (newValue) {
        setErrors((prev) => ({ ...prev, state: "" }));
      }
    } else {
      setState(newValue);
      if (!newValue) {
        setErrors((prev) => ({ ...prev, state: "State is required." }));
      } else {
        setErrors((prev) => ({ ...prev, state: "" }));
      }
    }
  };

  const handlePhoneChange = (event) => {
    const value = event.target.value.replace(/\D/g, "");
    setPhone(value);
    const phoneLength = countryCode.length;
    if (value.length !== phoneLength && value) {
      setErrors((prev) => ({
        ...prev,
        phone: `Please enter a valid phone number (${phoneLength} digits).`,
      }));
    } else {
      setErrors((prev) => ({ ...prev, phone: "" }));
    }
  };

  const handleNameChange = (event) => {
    const value = event.target.value;
    setName(value);
    if (value.length < 2) {
      setErrors((prev) => ({
        ...prev,
        name: "Please enter a name that's at least 2 characters.",
      }));
    } else {
      setErrors((prev) => ({ ...prev, name: "" }));
    }
  };

  const handleEmailChange = (event) => {
    const value = event.target.value;
    setEmail(value);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) && value) {
      setErrors((prev) => ({ ...prev, email: "Please enter a valid email address." }));
    } else {
      setErrors((prev) => ({ ...prev, email: "" }));
    }
  };

  const handleLanguageChange = (event, newValue) => {
    setLanguage(newValue || "");
    setLanguageInput(newValue || "");
    setErrors((prev) => ({ ...prev, language: "" }));
  };

  const handleLanguageInputChange = (event, newValue) => {
    setLanguageInput(newValue);
    setLanguage("");
    const filtered = languages.filter((lang) =>
      lang.toLowerCase().includes(newValue.toLowerCase())
    );
    setFilteredLanguages(filtered);
    if (newValue) {
      setErrors((prev) => ({ ...prev, language: "" }));
    }
  };

  const handleAddressLine1Change = (event) => {
    const value = event.target.value;
    setAddressLine1(value);
    if (!value) {
      setErrors((prev) => ({ ...prev, addressLine1: "Address Line 1 is required." }));
    } else {
      setErrors((prev) => ({ ...prev, addressLine1: "" }));
    }
  };

  const handleCityChange = (event) => {
    const value = event.target.value;
    setCity(value);
    if (!value) {
      setErrors((prev) => ({ ...prev, city: "City is required." }));
    } else {
      setErrors((prev) => ({ ...prev, city: "" }));
    }
  };

  const handlePincodeChange = (event) => {
    const value = event.target.value;
    setPincode(value);
    const pincodeFormat = pincodeFormats[country] || pincodeFormats.default;
    if (!pincodeFormat.regex.test(value) && value) {
      setErrors((prev) => ({
        ...prev,
        pincode: pincodeFormat.message,
      }));
    } else {
      setErrors((prev) => ({ ...prev, pincode: "" }));
    }
  };

  const handleCloseModal = () => {
    resetForm();
    handleClose();
  };

  const resetForm = () => {
    setName("");
    setEmail("");
    setPhone("");
    setCountryCode(countryCodes[0]);
    setLanguage("");
    setFilteredLanguages(languages);
    setLanguageInput("");
    setRole("");
    setRoleInput("");
    setLeadAgent("");
    setLeadAgentInput("");
    setAddressLine1("");
    setAddressLine2("");
    setCity("");
    setState("");
    setStateInput("");
    setCountry("");
    setCountryInput("");
    setCountryCodeISO("");
    setPincode("");
    setLeadSource("");
    setLeadSourceInput("");
    setErrors({});
    setIsSubmitting(false);
    setSubmitSuccess(false);
    setFilteredCountries(countries);
    setFilteredStates(states);
    setHasStates(true);
    setStateLoading(false);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!name || name.length < 2) newErrors.name = "Please enter a name that's at least 2 characters.";
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      newErrors.email = "Please enter a valid email address.";
    if (!phone || phone.length !== countryCode.length)
      newErrors.phone = `Please enter a valid phone number (${countryCode.length} digits).`;
    if (!language) newErrors.language = "Language is required.";
    if (!addressLine1) newErrors.addressLine1 = "Address Line 1 is required.";
    if (!city) newErrors.city = "City is required.";
    if (!state) newErrors.state = "State is required.";
    if (!country) newErrors.country = "Country is required.";
    const pincodeFormat = pincodeFormats[country] || pincodeFormats.default;
    if (!pincode || !pincodeFormat.regex.test(pincode))
      newErrors.pincode = pincodeFormat.message;
    if (!leadAgent) newErrors.leadAgent = "Lead Agent is required.";
    if (!leadSource) newErrors.leadSource = "Lead Source is required.";
    if (!role) newErrors.role = "Category is required.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveLead = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const formData = new FormData();

      formData.append("name", name);
      formData.append("email", email);
      formData.append("phone", `${countryCode.code.replace(/^\+/, "")}${phone}`);
      formData.append("language", language);
      const fullAddress = [addressLine1, addressLine2, city, state, country, pincode]
        .filter(Boolean)
        .join(", ");
      formData.append("address", fullAddress);
      formData.append("lead_agent", leadAgent);
      formData.append("leas_source", leadSource);
      formData.append("role", role);

      await leadsApis.add(formData);

      if (onLeadAdded) {
        onLeadAdded();
      }

      setIsSubmitting(false);
      setSubmitSuccess(true);
      setTimeout(() => {
        handleCloseModal();
      }, 1500);
    } catch (error) {
      console.error("Error saving lead:", error);
      setIsSubmitting(false);
      alert(`Failed to save lead: ${error.response?.data?.message || error.message}`);
    }
  };

  const pincodeFormat = pincodeFormats[country] || pincodeFormats.default;

  return (
    <Modal open={open} onClose={handleCloseModal} closeAfterTransition>
      <Slide direction="left" in={open}>
        <Box
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            width: "85%",
            height: "100%",
            bgcolor: "#f5f5f5",
            boxShadow: 24,
            p: 4,
            overflow: "auto",
            fontFamily: "Ubuntu, sans-serif",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <Typography
              variant="h3"
              component="h2"
              sx={{ color: "#212121", fontFamily: "Ubuntu, sans-serif" }}
            >
              New Lead
            </Typography>
            <Button
              onClick={handleCloseModal}
              variant="outlined"
              sx={{
                borderColor: "#bdbdbd",
                color: "#424242",
                "&:hover": {
                  borderColor: "#9e9e9e",
                  bgcolor: "rgba(0, 0, 0, 0.04)",
                },
                fontFamily: "Ubuntu, sans-serif",
              }}
            >
              Close
            </Button>
          </Box>
          <Card
            sx={{
              p: 2,
              bgcolor: "#ffffff",
              boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.08)",
            }}
          >
            <CardContent>
              <Box sx={{ mb: 4 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: "#212121",
                        mb: 2,
                        fontFamily: "Ubuntu, sans-serif",
                      }}
                    >
                      Personal Information
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography
                      variant="body1"
                      mb={1}
                      sx={{
                        color: "#212121",
                        fontFamily: "Ubuntu, sans-serif",
                      }}
                    >
                      Name <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      value={name}
                      onChange={handleNameChange}
                      placeholder="Enter name"
                      error={!!errors.name}
                      InputProps={{
                        sx: {
                          backgroundColor: "#f9f9f9",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: errors.name ? "#ff4d4f" : "#e0e0e0",
                          },
                          "& .MuiInputBase-input": {
                            color: "#333",
                          },
                          fontFamily: "Ubuntu, sans-serif",
                        },
                        endAdornment: errors.name && (
                          <InputAdornment position="end">
                            <ErrorIcon
                              sx={{
                                color: "#ff4d4f",
                                fontSize: 24,
                                borderRadius: "50%",
                                bgcolor: "transparent",
                              }}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                    {errors.name && (
                      <Typography
                        sx={{
                          color: "#ff4d4f",
                          fontSize: "0.75rem",
                          mt: 0.5,
                          fontFamily: "Ubuntu, sans-serif",
                        }}
                      >
                        {errors.name}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography
                      variant="body1"
                      mb={1}
                      sx={{
                        color: "#212121",
                        fontFamily: "Ubuntu, sans-serif",
                      }}
                    >
                      Email <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      value={email}
                      onChange={handleEmailChange}
                      placeholder="example@email.com"
                      type="email"
                      error={!!errors.email}
                      InputProps={{
                        sx: {
                          backgroundColor: "#f9f9f9",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: errors.email ? "#ff4d4f" : "#e0e0e0",
                          },
                          "& .MuiInputBase-input": {
                            color: "#333",
                          },
                          fontFamily: "Ubuntu, sans-serif",
                        },
                        endAdornment: errors.email && (
                          <InputAdornment position="end">
                            <ErrorIcon
                              sx={{
                                color: "#ff4d4f",
                                fontSize: 24,
                                borderRadius: "50%",
                                bgcolor: "transparent",
                              }}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                    {errors.email && (
                      <Typography
                        sx={{
                          color: "#ff4d4f",
                          fontSize: "0.75rem",
                          mt: 0.5,
                          fontFamily: "Ubuntu, sans-serif",
                        }}
                      >
                        {errors.email}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography
                      variant="body1"
                      mb={1}
                      sx={{
                        color: "#212121",
                        fontFamily: "Ubuntu, sans-serif",
                      }}
                    >
                      Language <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <FormControl fullWidth>
                      <Autocomplete
                        options={filteredLanguages}
                        getOptionLabel={(option) => option}
                        value={language || null}
                        onChange={handleLanguageChange}
                        onInputChange={handleLanguageInputChange}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Search language"
                            error={!!errors.language}
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon sx={{ color: "#757575" }} />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <>
                                  {errors.language && (
                                    <InputAdornment position="end">
                                      <ErrorIcon
                                        sx={{
                                          color: "#ff4d4f",
                                          fontSize: 24,
                                          borderRadius: "50%",
                                          bgcolor: "transparent",
                                        }}
                                      />
                                    </InputAdornment>
                                  )}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                              sx: {
                                ...dropdownInputStyles,
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: errors.language ? "#ff4d4f" : "#e0e0e0",
                                },
                              },
                            }}
                          />
                        )}
                        PopperProps={{
                          placement: "bottom-start",
                          modifiers: [
                            {
                              name: "flip",
                              enabled: false,
                            },
                            {
                              name: "preventOverflow",
                              options: {
                                altAxis: true,
                              },
                            },
                          ],
                          sx: {
                            "& .MuiAutocomplete-paper": dropdownMenuStyles,
                          },
                        }}
                      />
                    </FormControl>
                    {errors.language && (
                      <Typography
                        sx={{
                          color: "#ff4d4f",
                          fontSize: "0.75rem",
                          mt: 0.5,
                          fontFamily: "Ubuntu, sans-serif",
                        }}
                      >
                        {errors.language}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography
                      variant="body1"
                      mb={1}
                      sx={{
                        color: "#212121",
                        fontFamily: "Ubuntu, sans-serif",
                      }}
                    >
                      Phone No. <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Box display="flex" gap={3}>
                      <FormControl sx={{ minWidth: 180, maxWidth: 240 }}>
                        <Autocomplete
                          options={countryCodes}
                          getOptionLabel={(option) => `${option.code} (${option.name})`}
                          value={countryCode}
                          onChange={handleCountryCodeChange}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Code"
                              InputProps={{
                                ...params.InputProps,
                                sx: {
                                  ...dropdownInputStyles,
                                  minWidth: 180,
                                  maxWidth: 240,
                                  "& .MuiInputBase-input": {
                                    padding: "8px 0 8px 8px !important",
                                    width: "auto",
                                    overflow: "visible",
                                  },
                                },
                              }}
                            />
                          )}
                          renderOption={(props, option) => (
                            <li {...props}>
                              <Typography
                                sx={{
                                  fontFamily: "Ubuntu, sans-serif",
                                  color: "#fff",
                                }}
                              >
                                {option.code} ({option.name})
                              </Typography>
                            </li>
                          )}
                          PopperProps={{
                            sx: {
                              "& .MuiAutocomplete-paper": {
                                ...dropdownMenuStyles,
                                minWidth: "200px",
                              },
                            },
                          }}
                        />
                      </FormControl>
                      <TextField
                        fullWidth
                        value={phone}
                        onChange={handlePhoneChange}
                        placeholder={countryCode.example}
                        type="text"
                        error={!!errors.phone}
                        InputProps={{
                          sx: {
                            backgroundColor: "#f9f9f9",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: errors.phone ? "#ff4d4f" : "#e0e0e0",
                            },
                            "& .MuiInputBase-input": {
                              color: "#333",
                            },
                            fontFamily: "Ubuntu, sans-serif",
                          },
                          endAdornment: errors.phone && (
                            <InputAdornment position="end">
                              <ErrorIcon
                                sx={{
                                  color: "#ff4d4f",
                                  fontSize: 24,
                                  borderRadius: "50%",
                                  bgcolor: "transparent",
                                }}
                              />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                    {errors.phone && (
                      <Typography
                        sx={{
                          color: "#ff4d4f",
                          fontSize: "0.75rem",
                          mt: 0.5,
                          fontFamily: "Ubuntu, sans-serif",
                        }}
                      >
                        {errors.phone}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: "#212121",
                        mb: 2,
                        mt: 2,
                        fontFamily: "Ubuntu, sans-serif",
                      }}
                    >
                      Lead Information
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography
                      variant="body1"
                      mb={1}
                      sx={{
                        color: "#212121",
                        fontFamily: "Ubuntu, sans-serif",
                      }}
                    >
                      Lead Agent <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <FormControl fullWidth>
                      <Autocomplete
                        options={filteredLeadAgents}
                        getOptionLabel={(option) => option.name}
                        value={leadAgentOptions.find((agent) => agent.id === leadAgent) || null}
                        onChange={handleLeadAgentChange}
                        onInputChange={handleLeadAgentInputChange}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Search lead agent"
                            error={!!errors.leadAgent}
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon sx={{ color: "#757575" }} />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <>
                                  {errors.leadAgent && (
                                    <InputAdornment position="end">
                                      <ErrorIcon
                                        sx={{
                                          color: "#ff4d4f",
                                          fontSize: 24,
                                          borderRadius: "50%",
                                          bgcolor: "transparent",
                                        }}
                                      />
                                    </InputAdornment>
                                  )}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                              sx: {
                                ...dropdownInputStyles,
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: errors.leadAgent ? "#ff4d4f" : "#e0e0e0",
                                },
                              },
                            }}
                          />
                        )}
                        PopperProps={{
                          placement: "bottom-start",
                          modifiers: [
                            {
                              name: "flip",
                              enabled: false,
                            },
                            {
                              name: "preventOverflow",
                              options: {
                                altAxis: true,
                              },
                            },
                          ],
                          sx: {
                            "& .MuiAutocomplete-paper": dropdownMenuStyles,
                          },
                        }}
                      />
                    </FormControl>
                    {errors.leadAgent && (
                      <Typography
                        sx={{
                          color: "#ff4d4f",
                          fontSize: "0.75rem",
                          mt: 0.5,
                          fontFamily: "Ubuntu, sans-serif",
                        }}
                      >
                        {errors.leadAgent}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography
                      variant="body1"
                      mb={1}
                      sx={{
                        color: "#212121",
                        fontFamily: "Ubuntu, sans-serif",
                      }}
                    >
                      Lead Source <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <FormControl fullWidth>
                      <Autocomplete
                        options={filteredLeadSources}
                        getOptionLabel={(option) => option.name}
                        value={leadSourceOptions.find((source) => source.id === leadSource) || null}
                        onChange={handleLeadSourceChange}
                        onInputChange={handleLeadSourceInputChange}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Search lead source"
                            error={!!errors.leadSource}
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon sx={{ color: "#757575" }} />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <>
                                  {errors.leadSource && (
                                    <InputAdornment position="end">
                                      <ErrorIcon
                                        sx={{
                                          color: "#ff4d4f",
                                          fontSize: 24,
                                          borderRadius: "50%",
                                          bgcolor: "transparent",
                                        }}
                                      />
                                    </InputAdornment>
                                  )}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                              sx: {
                                ...dropdownInputStyles,
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: errors.leadSource ? "#ff4d4f" : "#e0e0e0",
                                },
                              },
                            }}
                          />
                        )}
                        PopperProps={{
                          placement: "bottom-start",
                          modifiers: [
                            {
                              name: "flip",
                              enabled: false,
                            },
                            {
                              name: "preventOverflow",
                              options: {
                                altAxis: true,
                              },
                            },
                          ],
                          sx: {
                            "& .MuiAutocomplete-paper": dropdownMenuStyles,
                          },
                        }}
                      />
                    </FormControl>
                    {errors.leadSource && (
                      <Typography
                        sx={{
                          color: "#ff4d4f",
                          fontSize: "0.75rem",
                          mt: 0.5,
                          fontFamily: "Ubuntu, sans-serif",
                        }}
                      >
                        {errors.leadSource}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography
                      variant="body1"
                      mb={1}
                      sx={{
                        color: "#212121",
                        fontFamily: "Ubuntu, sans-serif",
                      }}
                    >
                      Category <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <FormControl fullWidth>
                      <Autocomplete
                        options={categoryOptions}
                        getOptionLabel={(option) => option.name}
                        value={categoryOptions.find((option) => option.id === role) || null}
                        onChange={handleRoleChange}
                        onInputChange={handleRoleInputChange}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Search category"
                            error={!!errors.role}
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon sx={{ color: "#757575" }} />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <>
                                  {errors.role && (
                                    <InputAdornment position="end">
                                      <ErrorIcon
                                        sx={{
                                          color: "#ff4d4f",
                                          fontSize: 24,
                                          borderRadius: "50%",
                                          bgcolor: "transparent",
                                        }}
                                      />
                                    </InputAdornment>
                                  )}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                              sx: {
                                ...dropdownInputStyles,
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: errors.role ? "#ff4d4f" : "#e0e0e0",
                                },
                              },
                            }}
                          />
                        )}
                        PopperProps={{
                          placement: "bottom-start",
                          modifiers: [
                            {
                              name: "flip",
                              enabled: false,
                            },
                            {
                              name: "preventOverflow",
                              options: {
                                altAxis: true,
                              },
                            },
                          ],
                          sx: {
                            "& .MuiAutocomplete-paper": dropdownMenuStyles,
                          },
                        }}
                      />
                    </FormControl>
                    {errors.role && (
                      <Typography
                        sx={{
                          color: "#ff4d4f",
                          fontSize: "0.75rem",
                          mt: 0.5,
                          fontFamily: "Ubuntu, sans-serif",
                        }}
                      >
                        {errors.role}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: "#212121",
                        mb: 2,
                        mt: 2,
                        fontFamily: "Ubuntu, sans-serif",
                      }}
                    >
                      Address Information
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography
                      variant="body1"
                      mb={1}
                      sx={{
                        color: "#212121",
                        fontFamily: "Ubuntu, sans-serif",
                      }}
                    >
                      Country <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <FormControl fullWidth>
                      <Autocomplete
                        options={filteredCountries}
                        getOptionLabel={(option) => option.name}
                        value={countries.find((c) => c.name === country) || null}
                        onChange={handleCountryChange}
                        onInputChange={handleCountryInputChange}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Search country"
                            error={!!errors.country}
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon sx={{ color: "#757575" }} />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <>
                                  {errors.country && (
                                    <InputAdornment position="end">
                                      <ErrorIcon
                                        sx={{
                                          color: "#ff4d4f",
                                          fontSize: 24,
                                          borderRadius: "50%",
                                          bgcolor: "transparent",
                                        }}
                                      />
                                    </InputAdornment>
                                  )}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                              sx: {
                                ...dropdownInputStyles,
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: errors.country ? "#ff4d4f" : "#e0e0e0",
                                },
                              },
                            }}
                          />
                        )}
                        PopperProps={{
                          placement: "bottom-start",
                          modifiers: [
                            {
                              name: "flip",
                              enabled: false,
                            },
                            {
                              name: "preventOverflow",
                              options: {
                                altAxis: true,
                              },
                            },
                          ],
                          sx: {
                            "& .MuiAutocomplete-paper": dropdownMenuStyles,
                          },
                        }}
                      />
                    </FormControl>
                    {errors.country && (
                      <Typography
                        sx={{
                          color: "#ff4d4f",
                          fontSize: "0.75rem",
                          mt: 0.5,
                          fontFamily: "Ubuntu, sans-serif",
                        }}
                      >
                        {errors.country}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography
                      variant="body1"
                      mb={1}
                      sx={{
                        color: "#212121",
                        fontFamily: "Ubuntu, sans-serif",
                      }}
                    >
                      Address Line 1 <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      value={addressLine1}
                      onChange={handleAddressLine1Change}
                      placeholder="Enter address line 1"
                      error={!!errors.addressLine1}
                      InputProps={{
                        sx: {
                          backgroundColor: "#f9f9f9",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: errors.addressLine1 ? "#ff4d4f" : "#e0e0e0",
                          },
                          "& .MuiInputBase-input": {
                            color: "#333",
                          },
                          fontFamily: "Ubuntu, sans-serif",
                        },
                        endAdornment: errors.addressLine1 && (
                          <InputAdornment position="end">
                            <ErrorIcon
                              sx={{
                                color: "#ff4d4f",
                                fontSize: 24,
                                borderRadius: "50%",
                                bgcolor: "transparent",
                              }}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                    {errors.addressLine1 && (
                      <Typography
                        sx={{
                          color: "#ff4d4f",
                          fontSize: "0.75rem",
                          mt: 0.5,
                          fontFamily: "Ubuntu, sans-serif",
                        }}
                      >
                        {errors.addressLine1}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography
                      variant="body1"
                      mb={1}
                      sx={{
                        color: "#212121",
                        fontFamily: "Ubuntu, sans-serif",
                      }}
                    >
                      Address Line 2
                    </Typography>
                    <TextField
                      fullWidth
                      value={addressLine2}
                      onChange={(e) => setAddressLine2(e.target.value)}
                      placeholder="Enter address line 2 (optional)"
                      InputProps={{
                        sx: {
                          backgroundColor: "#f9f9f9",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e0e0e0",
                          },
                          "& .MuiInputBase-input": {
                            color: "#333",
                          },
                          fontFamily: "Ubuntu, sans-serif",
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography
                      variant="body1"
                      mb={1}
                      sx={{
                        color: "#212121",
                        fontFamily: "Ubuntu, sans-serif",
                      }}
                    >
                      City <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      value={city}
                      onChange={handleCityChange}
                      placeholder="Enter city"
                      error={!!errors.city}
                      InputProps={{
                        sx: {
                          backgroundColor: "#f9f9f9",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: errors.city ? "#ff4d4f" : "#e0e0e0",
                          },
                          "& .MuiInputBase-input": {
                            color: "#333",
                          },
                          fontFamily: "Ubuntu, sans-serif",
                        },
                        endAdornment: errors.city && (
                          <InputAdornment position="end">
                            <ErrorIcon
                              sx={{
                                color: "#ff4d4f",
                                fontSize: 24,
                                borderRadius: "50%",
                                bgcolor: "transparent",
                              }}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                    {errors.city && (
                      <Typography
                        sx={{
                          color: "#ff4d4f",
                          fontSize: "0.75rem",
                          mt: 0.5,
                          fontFamily: "Ubuntu, sans-serif",
                        }}
                      >
                        {errors.city}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography
                      variant="body1"
                      mb={1}
                      sx={{
                        color: "#212121",
                        fontFamily: "Ubuntu, sans-serif",
                      }}
                    >
                      State/Region/Province <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <FormControl fullWidth>
                      {country ? (
                        hasStates ? (
                          <Autocomplete
                            options={filteredStates}
                            value={state || null}
                            onChange={handleStateChange}
                            onInputChange={handleStateInputChange}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Search state"
                                disabled={!country}
                                error={!!errors.state}
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <SearchIcon sx={{ color: "#757575" }} />
                                    </InputAdornment>
                                  ),
                                  endAdornment: (
                                    <>
                                      {errors.state && (
                                        <InputAdornment position="end">
                                          <ErrorIcon
                                            sx={{
                                              color: "#ff4d4f",
                                              fontSize: 24,
                                              borderRadius: "50%",
                                              bgcolor: "transparent",
                                            }}
                                          />
                                        </InputAdornment>
                                      )}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                  sx: {
                                    ...dropdownInputStyles,
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor: errors.state ? "#ff4d4f" : "#e0e0e0",
                                    },
                                  },
                                }}
                              />
                            )}
                            PopperProps={{
                              placement: "bottom-start",
                              modifiers: [
                                {
                                  name: "flip",
                                  enabled: false,
                                },
                                {
                                  name: "preventOverflow",
                                  options: {
                                    altAxis: true,
                                  },
                                },
                              ],
                              sx: {
                                "& .MuiAutocomplete-paper": dropdownMenuStyles,
                              },
                            }}
                          />
                        ) : (
                          <TextField
                            fullWidth
                            value={state}
                            onChange={(e) => handleStateInputChange(e, e.target.value)}
                            placeholder="Enter state"
                            disabled={!country}
                            error={!!errors.state}
                            InputProps={{
                              sx: {
                                ...dropdownInputStyles,
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: errors.state ? "#ff4d4f" : "#e0e0e0",
                                },
                              },
                              endAdornment: errors.state && (
                                <InputAdornment position="end">
                                  <ErrorIcon
                                    sx={{
                                      color: "#ff4d4f",
                                      fontSize: 24,
                                      borderRadius: "50%",
                                      bgcolor: "transparent",
                                    }}
                                  />
                                </InputAdornment>
                              ),
                            }}
                          />
                        )
                      ) : (
                        <TextField
                          fullWidth
                          value=""
                          placeholder="Enter or choose state"
                          disabled
                          InputProps={{
                            sx: {
                              backgroundColor: "#f9f9f9",
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#e0e0e0",
                              },
                              "& .MuiInputBase-input": {
                                color: "#757575",
                              },
                              fontFamily: "Ubuntu, sans-serif",
                            },
                          }}
                        />
                      )}
                    </FormControl>
                    {errors.state && (
                      <Typography
                        sx={{
                          color: "#ff4d4f",
                          fontSize: "0.75rem",
                          mt: 0.5,
                          fontFamily: "Ubuntu, sans-serif",
                        }}
                      >
                        {errors.state}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography
                      variant="body1"
                      mb={1}
                      sx={{
                        color: "#212121",
                        fontFamily: "Ubuntu, sans-serif",
                      }}
                    >
                      Pincode <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      value={pincode}
                      onChange={handlePincodeChange}
                      placeholder={pincodeFormat.example}
                      error={!!errors.pincode}
                      InputProps={{
                        sx: {
                          backgroundColor: "#f9f9f9",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: errors.pincode ? "#ff4d4f" : "#e0e0e0",
                          },
                          "& .MuiInputBase-input": {
                            color: "#333",
                          },
                          fontFamily: "Ubuntu, sans-serif",
                        },
                        endAdornment: errors.pincode && (
                          <InputAdornment position="end">
                            <ErrorIcon
                              sx={{
                                color: "#ff4d4f",
                                fontSize: 24,
                                borderRadius: "50%",
                                bgcolor: "transparent",
                              }}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                    {errors.pincode && (
                      <Typography
                        sx={{
                          color: "#ff4d4f",
                          fontSize: "0.75rem",
                          mt: 0.5,
                          fontFamily: "Ubuntu, sans-serif",
                        }}
                      >
                        {errors.pincode}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </Box>

              <Box display="flex" gap={2} mt={3}>
                <Button
                  variant="contained"
                  onClick={saveLead}
                  sx={{
                    bgcolor: "#4285F4",
                    color: "white",
                    "&:hover": {
                      bgcolor: "#3367D6",
                    },
                    fontWeight: 500,
                    textTransform: "none",
                    padding: "8px 16px",
                    fontFamily: "Ubuntu, sans-serif",
                    transition: "background-color 0.3s ease",
                  }}
                >
                  Save Lead
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleCloseModal}
                  sx={{
                    borderColor: "#bdbdbd",
                    color: "#424242",
                    "&:hover": {
                      borderColor: "#9e9e9e",
                      bgcolor: "rgba(0, 0, 0, 0.04)",
                    },
                    textTransform: "none",
                    padding: "8px 16px",
                    fontFamily: "Ubuntu, sans-serif",
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Slide>
    </Modal>
  );
}